import type {Subscription} from "rxjs";
import {from, map} from "rxjs";
import type {AxiosError, AxiosResponse} from "axios";
import {isAxiosError} from "axios";
import type {PageResponse} from "../../services/ApiTypes";
import {makeAutoObservable, runInAction, toJS} from "mobx";
import type React from "react";
import {rollbar} from "../../env";
import {axiosErrorToString} from "../../utils/axios.utils";

export class PageResponseManager<RequestData = void, Data = unknown, ResponseData = Data> {
    data?: Data[]

    isLoading = false
    error: null | string = null
    page = 1
    count = 0
    hasNext = false
    hasPrev = false

    private lastRequestData: RequestData | undefined;

    req?: Subscription;

    constructor(
        public pageSize: number,
        private readonly requester: (page: number, request: RequestData) => Promise<AxiosResponse<PageResponse<ResponseData>>>,
        private readonly parser?: (data: ResponseData, request: RequestData) => Data,
    ) {
        makeAutoObservable(this, {req: false})
    }

    init(request: RequestData) {
        console.log('init', request);
        this.page = 1;
        this.count = 0;
        this.hasNext = false;
        this.hasPrev = false;
        this.data = undefined;
        this.error = null;
        this._request(request);
    }

    _request(request: RequestData) {
        console.log('PageResponseManager: request')
        this.lastRequestData = request;
        this.isLoading = true;
        this.error = null;
        if (this.req) this.req.unsubscribe();

        this.req = from(this.requester(this.page, request))
            .pipe(
                map(resp => {
                    console.assert(Array.isArray(resp.data.results))
                    if (resp.data.results.length === 0) {
                        // No data present
                        return ({data: [], count: 0, hasNext: false, hasPrev: false});
                    }
                    let data: Data[];
                    if (this.parser) {
                        const parser = this.parser.bind(this);
                        data = resp.data.results.map(r => parser(r, request) as Data) as Data[];
                    } else {
                        data = resp.data.results as unknown as Data[];
                    }
                    return ({
                        data: data,
                        count: resp.data.count,
                        hasNext: Boolean(resp.data.next),
                        hasPrev: Boolean(resp.data.previous),
                    });
                }),
            )
            .subscribe({
                next: ({data, count, hasNext, hasPrev}) => runInAction(() => {
                    this.error = null;
                    this.data = data;
                    this.count = count;
                    this.hasNext = hasNext;
                    this.hasPrev = hasPrev;
                }),
                error: err => runInAction(() => {
                    rollbar.error(err)
                    this.error = axiosErrorToString(err);
                    this.isLoading = false;
                }),
                complete: () => runInAction(() => {
                    this.isLoading = false;
                }),
            })
    }

    changePage(page: number) {
        if (!this.lastRequestData) {
            console.warn('Cannot change page', page, this.lastRequestData);
            return
        }
        this.setPage(page, this.lastRequestData);
    }

    onRowsPerPageChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
        const pageSize = parseInt(event.target.value);
        if (!this.lastRequestData) {
            console.warn('Cannot change pageSize', pageSize, this.lastRequestData);
            return
        }
        const request = toJS(this.lastRequestData);
        this.pageSize = pageSize;
        this.page = 1;
        this._request(request);
    }

    setPage(page: number, request: RequestData) {
        request = toJS(request);
        console.log('setPage', {page, request});
        this.page = page
        this._request(request);
    }

    reset() {
        this.page = 1;
        this.count = 0;
        this.hasNext = false;
        this.hasPrev = false;
        this.data = undefined;
        this.isLoading = false;
        this.error = null;
        this.lastRequestData = undefined;
        if (this.req) this.req.unsubscribe();
    }

    reloadView() {
        if (this.lastRequestData) {
            this._request(this.lastRequestData);
        }
    }
}
